import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './CariHesaplarV2Page.css';
import { useParams, useNavigate } from 'react-router-dom';
import { sanitizeSearchInput, toValidObjectIdString } from '../utils/security';
import { fetchTeknikServisler } from '../services/cariV2Service';
import CariHareketlerPanel from '../components/CariHareketlerPanel';
import TableListFooter from '../components/TableListFooter';
import { normalizeSayfalama } from '../utils/pagination';
import { formatSignedBalance } from '../utils/formatBalance';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '—';
  return `${parseFloat(amount).toFixed(2)} ₺`;
};

const getTechServiceId = (ts) => toValidObjectIdString(ts?.id ?? ts?._id) || (typeof ts?.id === 'string' && /^[0-9a-fA-F]{24}$/.test(ts.id) ? ts.id : null);

const TS_LISTE_LIMIT = 25;

const CariHesaplarV2Page = ({ forceTeknikServisId, singleMode }) => {
  const { id: paramId } = useParams();
  const id = forceTeknikServisId || paramId;
  const navigate = useNavigate();
  const [teknikServisler, setTeknikServisler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({ search: '' });
  const [tsListeSayfa, setTsListeSayfa] = useState(1);
  const [tsListeMeta, setTsListeMeta] = useState({ total: 0, totalPages: 1 });

  const sanitizedSearch = useMemo(
    () => (filters.search && filters.search.trim() ? filters.search.trim() : ''),
    [filters.search]
  );

  const loadTeknikServisler = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchTeknikServisler({
        arama: sanitizedSearch || undefined,
        sayfa: tsListeSayfa,
        limit: TS_LISTE_LIMIT,
      });
      if (response?.basarili && response?.veri) {
        setTeknikServisler(response.veri.teknikServisler || []);
        setTsListeMeta(normalizeSayfalama(response.veri.sayfalama));
      }
    } catch {
      setTeknikServisler([]);
      setTsListeMeta({ total: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, [sanitizedSearch, tsListeSayfa]);

  const validId = toValidObjectIdString(id) || (typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id) ? id : null);

  useEffect(() => {
    if (id) {
      const tid = toValidObjectIdString(id) || (typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id) ? id : null);
      if (!tid && !singleMode) {
        navigate('/cari-hesaplar');
      }
    } else if (!singleMode) {
      loadTeknikServisler();
    }
  }, [id, singleMode, loadTeknikServisler, navigate]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput }));
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    setTsListeSayfa(1);
  }, [sanitizedSearch]);

  if (validId) {
    return (
      <CariHareketlerPanel
        teknikServisId={validId}
        isTechView={!!singleMode}
        onBack={singleMode ? undefined : () => navigate('/cari-hesaplar')}
      />
    );
  }

  if (singleMode) {
    return (
      <main className="main-content">
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-gray)' }}>
          Yükleniyor...
        </div>
      </main>
    );
  }

  return (
    <main className="main-content">
      <div className="content-header">
        <h1>Cari Hareketler</h1>
      </div>

      <div className="toolbar" style={{ marginBottom: '16px' }}>
        <div className="search-box" style={{ flex: 1, maxWidth: '350px' }}>
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Teknik servis adı ile ara..."
            value={searchInput}
            onChange={(e) => setSearchInput(sanitizeSearchInput(e.target.value, { trimEdges: false }))}
            maxLength={100}
            autoComplete="off"
          />
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>Yükleniyor...</div>
        ) : teknikServisler.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-gray)' }}>
            Teknik servis bulunamadı
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>Teknik Servis</th>
                    <th>Kod</th>
                    <th style={{ textAlign: 'right' }}>Bakiye</th>
                    <th className="islemler-sutunu">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {teknikServisler.map((ts) => (
                    <tr key={ts.id}>
                      <td style={{ fontWeight: 500 }}>{ts.ad || '—'}</td>
                      <td>{ts.kod || '—'}</td>
                      <td style={{ textAlign: 'right', color: (ts.bakiye || 0) !== 0 ? ((ts.bakiye || 0) > 0 ? 'var(--success-green)' : 'var(--error)') : undefined }}>
                        {formatSignedBalance(ts.bakiye || 0, formatCurrency)}
                      </td>
                      <td className="cari-islemler-cell islemler-sutunu">
                        <button
                          type="button"
                          className="category-action-btn edit-btn"
                          onClick={() => {
                            const tid = getTechServiceId(ts);
                            if (tid) navigate(`/cari-hesaplar/teknik-servis/${tid}`);
                            else window.showNotification?.('error', 'Teknik servis ID alınamadı');
                          }}
                          title="Hareketler"
                        >
                          <i className="fas fa-list"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {tsListeMeta.totalPages <= 1 && tsListeMeta.total > 0 && (
              <div style={{ marginTop: '12px', padding: '0 4px', fontSize: '14px', color: 'var(--text-gray)' }}>
                Toplam {tsListeMeta.total} teknik servis
              </div>
            )}
            <TableListFooter
              totalRecords={tsListeMeta.total}
              currentPage={tsListeSayfa}
              totalPages={tsListeMeta.totalPages || 1}
              onPageChange={setTsListeSayfa}
              loading={loading}
              embedded
            />
          </>
        )}
      </div>
    </main>
  );
};

export default CariHesaplarV2Page;
